import {map, timeout} from 'rxjs/operators';
import {EventEmitter, Injectable} from '@angular/core';
import {HttpService} from '../http-service.service';
import {ApiConst} from '../api-const';
import {Observable} from 'rxjs';
import {AuthenticationService} from '../security/authentication.service';
import {TreatmentChainService} from './treatment-chain.service';
import {Lre} from '../../../model/lre';
import {Spool} from '../../../model/spool';
import {FileStatus} from '../../../application/application-container/upload/helpers/file-status.enum';
import {KindOfUpload} from '../../../application/application-container/upload/helpers/kind-of-upload.enum';
import {CommonService} from './common.service';

/**
 * Service that handle the new deposit creation
 */
@Injectable()
export class CreateDepositService {

  public customerSpoolList: EventEmitter<Spool[]>;
  public runningExpressDeposit: EventEmitter<string>;
  /**
   * @param {HttpService} httpService - handle http request
   * @param {AuthenticationService} authenticationService - handle info of connected user
   * @param commonService
   * @param {TreatmentChainService} treatmentChainService - handle default treatmentChain
   */
  constructor(private httpService: HttpService,
              private authenticationService: AuthenticationService,
              private commonService: CommonService,
              private treatmentChainService: TreatmentChainService) {
    this.customerSpoolList = new EventEmitter();
    this.runningExpressDeposit = new EventEmitter();
  }

  public orderFinalFileList(fileList: any[]): any[] {
    const result:any[] = [];
    this.orderFileListByKind(result, fileList, FileStatus.PRINCIPAL);
    this.orderFileListByKind(result, fileList, FileStatus.ANNEXE);
    this.orderFileListByKind(result, fileList, FileStatus.SPOOL);
    this.orderFileListByKind(result, fileList, FileStatus.DATA);
    return result;
  }
  public orderFileListByKind(mainList: any[], fileList: any[], type: string) {
    for (const file of fileList) {
      if (file.kindOfFile === type) {
        mainList.push(file);
      }
    }
  }
  /**
   *
   * Perform the upload request
   *
   * @param {string} depositNature - kind of deposit : Letter/ Mailin (...)
   * @param {string} treatmentChain - selected treatment chain
   * @param {any[]} fileList - list of files with kindofFile and index
   * @param spoolId
   * @param delayedDate
   * @return the request result
   */
  public createNewDeposit(depositNature: string, treatmentChain: string, fileList: any[], spoolId: string, delayedDate: Date): Observable<any> {
    let sDptNature = depositNature;
    const dataToSend = new FormData();
    let principalFilesCount = 0;
    const orderedList = this.orderFinalFileList(fileList);
    for (let i = 0; i < orderedList.length; i++) {
      dataToSend.append('file', orderedList[i].file);
      if (orderedList[i].kindOfFile === FileStatus.PRINCIPAL) {
        principalFilesCount++;
      }
    }
    if (principalFilesCount > 1) {
      sDptNature = KindOfUpload.MULTI_UNIT;
    }

    dataToSend.append('sDepotFilesList', JSON.stringify({
      objAuthEnvt: this.getUser(),
      sDptNature: sDptNature,
      objDepotCriteria: this.buildDepositCriteria(treatmentChain, spoolId, delayedDate),
      listObjDepotFile: this.buildDepositFiles(orderedList),
    }));
    return this.httpService.$post(ApiConst.uploadNewDeposit, dataToSend).pipe(map((response: Response) => {
      return response;
    }));

  }

  /**
   * get the currently logged user
   *
   * @return User object formatted for upload request
   */
  private getUser(): any {

    const tempUser = this.authenticationService.userLogged;
    return {
      'sCustomerID': tempUser.idCustomer,
      'sUserID': tempUser.idUser
    };
  }

  /**
   * build criteria for upload
   * @param {string} treatmentChain - selected treatmentChain
   * @param sSpoolId
   * @param delayedDate
   * @return the correct object for upload request
   */
  private buildDepositCriteria(treatmentChain: string, sSpoolId: string, delayedDate: Date): any {
    let spool = '';
    if ('_NA_' !== sSpoolId) {
      spool = sSpoolId;
    }
    let bTrtAuto;
    let sTrtChainName = '';

    if (treatmentChain === this.treatmentChainService.defaultTreatmentChain) {
      bTrtAuto = true;
    } else {
      bTrtAuto = false;
      sTrtChainName = treatmentChain;
    }
    let delayedDateString = '';
    if (delayedDate != null) {
      const month = delayedDate.getMonth() + 1;
      const mm = (month < 10) ? '0' + month : month;
      const dd = (delayedDate.getDate() < 10) ? '0' + delayedDate.getDate() : delayedDate.getDate();
      delayedDateString = delayedDate.getFullYear() + '-' + mm + '-' + dd;
    }
    return {
      'sSpoolId': spool,
      'bTrtAuto': bTrtAuto,
      'sTrtChainName': sTrtChainName,
      'bPostalTrt': true,
      'sPostalTrtPostage': 'NONE',
      'sPostalTrtEnvelope': 'NONE',
      'bPostalTrtColor': false,
      'bPostalTrtAdressPage': false,
      'bEmailTrt': false,
      'sEmailTrtDest': '',
      'iEmailTrtWait': 0,
      'bEmailTrtPostal': false,
      'bFaxTrt': false,
      'sFaxTrtDest': '',
      'iFaxTrtTry': '',
      'bFaxTrtPostal': false,
      'bElectroArchivTrt': false,
      'sElectroArchivTrtID': '',
      'bDelayedSend': delayedDate != null,
      'sDelayedSendDate': delayedDateString
    };
  }

  /**
   * get the fileList for upload request
   *
   * @param  fileList - list of files
   * @return corrected list of files object
   */
  private buildDepositFiles(fileList: any[]): any {
    const myReturn = [];
    for (const file of fileList) {
      myReturn.push({
        'sDptType': file.kindOfFile,
        'dDepotDateTime': new Date(),
        'iOrgNumberOfCopies': 1,
        'sFileName': file.file.name,
        'iDptIdx': file.index
      });
    }

    return myReturn;
  }
  public getLREDefaultSender(): Observable<any> {
    return this.httpService.$post(ApiConst.getLREDefaultSender, '').pipe(map(response => {
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  /**
   *
   * create lre deposit
   *
   * @param lreObject - ERL json object
   * @param treatmentChain - selected treatment chain
   * @param fileList - list of files with kindofFile and index
   * @return - the server response
   */
  public createLREDeposit(lreObject: Lre, treatmentChain: string, fileList: any[]): Observable<any> {
    const dataToSend = new FormData();

    for (let i = 0; i < fileList.length; i++) {
      dataToSend.append('file', fileList[i].file);
    }
    if (treatmentChain && treatmentChain !== this.treatmentChainService.defaultTreatmentChain) {
      lreObject.trtChainName = treatmentChain;
    }
    dataToSend.append('erl', lreObject.stringify());

    return this.httpService.$post(ApiConst.uploadLreDeposit, dataToSend).pipe(map((response: Response) => {
      return response;
    }));

  }
  public createSpool(spoolName: string, xmlFileName: string, file: any): Observable<any> {

    const dataToSend = new FormData();
    dataToSend.append('file', file);
    dataToSend.append('spoolName', spoolName);
    dataToSend.append('xmlFileName', xmlFileName);

    return this.httpService.$post(ApiConst.createSpool, dataToSend).pipe(map(response => {
      this.customerSpoolList.emit(response.body.datas.spools);
      return response;
    }));

  }

  public deleteSpool(spoolId: string): Observable<any> {

    const dataToSend = new FormData();
    dataToSend.append('spoolId', spoolId);

    return this.httpService.$post(ApiConst.deleteSpool, dataToSend).pipe(map(response => {
      this.customerSpoolList.emit(response.body.datas.spools);
      return response;
    }));
  }

  /*public prepareExpressMainFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.prepareExpressMainFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }*/
  public prepareMailingFinalFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.prepareMailingFinalFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public prepareUnitFinalFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.prepareUnitFinalFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public prepareErlFinalFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.prepareErlFinalFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public setExpressDepositMargins(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.setExpressDepositMargins, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public prepareSpoolFinalFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.prepareSpoolFinalFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
 public getExpressDepositPostages(cfeId: string): Observable<any> {
    return this.httpService.$post(ApiConst.getExpressDepositPostages, { cfeId: cfeId }).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }

  public getExpressDepositEncart(customerId: string): Observable<any> {
    return this.httpService.$post(ApiConst.getExpressDepositEncarts, { customerId }).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }

  public getExpressDepositTemplates(): Observable<any> {
    return this.httpService.$post(ApiConst.getExpressDepositTemplates, {}).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }

  public getCustomerCFEId(): Observable<string> {
    return this.httpService.$post(ApiConst.getCustomerCFEId, {}).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : '';
    }));
  }
  public storeDepositOptions(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.storeDepositOptions, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public addExpressMainFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.addExpressMainFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public addExpressSpoolFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.addExpressSpoolFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public storeSpoolSplitInfo(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.storeSpoolSplitInfo, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public addExpressMailingModel(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.addExpressMailingModel, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public prepareSpoolSignature(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.prepareSpoolSignature, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public checkDataFile(dataToSend: FormData): Observable<any> {
    return this.httpService.$post(ApiConst.checkDataFile, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  /*public addFDP(file: File, fdp: File): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('main', file);
    dataToSend.append('fdp', fdp);
    return this.httpService.$post(ApiConst.addFDP, dataToSend).pipe(map(response => {
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }*/
  public getPageAsImage(depositDir: string, pageNumber: number): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('depositDir', depositDir);
    dataToSend.append('pageNumber', '' + pageNumber);
    return this.httpService.$post(ApiConst.getPageAsImage, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  /*public prepareMarginCorrection(dataToSend: any): Observable<any> {
    return this.httpService.$post(ApiConst.prepareMarginCorrection, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }*/

  public storeRecipients(dataToSend: any): Observable<any> {
    return this.httpService.$post(ApiConst.storeRecipients, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public storeSignaturePosition(dataToSend: any): Observable<any> {
    return this.httpService.$post(ApiConst.storeSignaturePosition, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }

  public saveExpressDeposit(dataToSend: any): Observable<any> {
    return this.httpService.$post(ApiConst.saveExpressDeposit, dataToSend).pipe(
      timeout(600000),
      map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public checkExpressDepositProgress(dataToSend): Observable<any> {
    return this.httpService.$post(ApiConst.checkExpressDepositProgress, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }

  public extractExpressAddress( data: any): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('areaInfo', JSON.stringify(data));
    return this.httpService.$post(ApiConst.extractExpressAddress, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  /*public extractSpoolAddresses( data: any): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('areaInfo', JSON.stringify(data));
    return this.httpService.$post(ApiConst.extractSpoolAddresses, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }*/
/*  public splitExpressFile( data: any): Observable<any> {
    return this.httpService.$post(ApiConst.splitExpressFile, data).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public getSpoolFoldImages( data: any): Observable<any> {
    return this.httpService.$post(ApiConst.getSpoolFoldImages, data).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public extractSplitAddresses( data: any): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('areaInfo', JSON.stringify(data));
    return this.httpService.$post(ApiConst.extractSplitAddresses, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public createNewCustomerModel(modelName: string, content: string): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('modelName', modelName);
    dataToSend.append('content', modelName);
    return this.httpService.$post(ApiConst.createNewCustomerModel, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
    public saveCustomerModel(modelName: string, content: string): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('modelName', modelName);
    dataToSend.append('content', content);
    return this.httpService.$post(ApiConst.saveCustomerModel, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }*/
  public createNewLOOLCustomerModel(depositDir: string, addressLines?: string[]): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('depositDir', depositDir);
    if (addressLines) {
      dataToSend.append('addressLines', JSON.stringify(addressLines));
    }
    return this.httpService.$post(ApiConst.createNewCustomerModel, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }
  public getUnitCustomerModelPDF(depositDir: string): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('depositDir', depositDir);
    return this.httpService.$post(ApiConst.getUnitCustomerModelPDF, dataToSend).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }

  public getExpressDepositInProgress(): Observable<any> {
    return this.httpService.$post(ApiConst.getExpressDepositInProgress, {}).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas ? response.body.datas : {};
    }));
  }

  public verifyDefaultTreatmentChainExist(): Observable<boolean|null>{
    return this.httpService.$post(ApiConst.verifyDefaultTreatmentChainExist, {}).pipe(map(response => {
      if (response.body && response.body.sMsgError && !response.body.bOK ) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: 'Attention:',
          detail: response.body.sMsgError
        });
      }
      return response.body && response.body.datas && response.body.datas.value ? response.body.datas.value : null;
    }));
  }

}
